import { FetchResponse } from '@zapier/identity-fetch';
import has from 'lodash/has';

import { LoginErrors, LoginResult, LoginResultType } from './types';
import {
  ACCOUNT_LOCKED_ERROR,
  EMAIL_VERIFICATION_REQUIRED_ERROR,
  GENERIC_ERROR_MESSAGE,
  MFA_ERROR,
  PENDING_WORKSPACE_ACCESS_ERROR,
  SINGLE_SESSION_ERROR,
} from './constants';
import { urls } from '@zapier/identity-utils';

function hasErrorsOfType(type: string, errors: any) {
  return (
    Array.isArray(errors) && errors.some((error: any) => error.type === type)
  );
}

const fieldConversions: Record<string, string> = {
  captcha_required: 'captcha',
};

export function parseLoginErrors(resp: FetchResponse<LoginResult>) {
  const parsedErrors: LoginErrors = {};
  let resultType = LoginResultType.IS_VALID_LOGIN;
  let redirectUrl = '';

  try {
    const errors = resp.body.errors;

    if (!resp.headers?.get('content-type')?.includes('application/json')) {
      // The server responded with HTML which normally means cookies have
      // been disabled
      parsedErrors.error = 'It looks like you have cookies disabled';
      resultType = LoginResultType.IS_MISSING_CSRF;
      redirectUrl = urls.COOKIE_ERROR_URL;
    }

    if (resp.status === 429) {
      // The server says we are trying to often
      parsedErrors.error =
        'It looks like you are trying to login too often. If you’re having trouble, please contact support.';
      resultType = LoginResultType.TRYING_TOO_OFTEN;
    } else if (resp.status === 401) {
      // The user entered invalid credentials
      resultType = LoginResultType.IS_BAD_CREDENTIALS;
    } else if (resp.status === 403) {
      // 403s carry a typed error in the response body telling us why the
      // user can authenticate but not proceed. Match on the type to decide
      // where to route them; fall through to the cookie/CSRF fallback for
      // an unknown or empty type.
      if (hasErrorsOfType(ACCOUNT_LOCKED_ERROR, errors)) {
        // The account the user is trying to log in to was locked
        parsedErrors.error = 'Your account has been locked.';
        resultType = LoginResultType.IS_LOCKED;
        redirectUrl = urls.ACCOUNT_LOCKED_URL;
      } else if (hasErrorsOfType(MFA_ERROR, errors)) {
        // The credentials were good but MFA is required to fully log in
        resultType = LoginResultType.IS_2FA_REQUIRED;
        redirectUrl = urls.MFA_URL;
      } else if (hasErrorsOfType(SINGLE_SESSION_ERROR, errors)) {
        // User is logged in with multiple sessions
        parsedErrors.error = 'A single session is required.';
        resultType = LoginResultType.IS_SINGLE_SESSION;
        redirectUrl = urls.SINGLE_SESSION_URL;
      } else if (hasErrorsOfType(PENDING_WORKSPACE_ACCESS_ERROR, errors)) {
        // User authenticated but is only on org-managed HQs without
        // workspace access yet. Backend has already cleared the session;
        // route the user to the pending workspace access page.
        resultType = LoginResultType.IS_PENDING_WORKSPACE_ACCESS;
        redirectUrl = urls.PENDING_WORKSPACE_ACCESS_URL;
      } else if (hasErrorsOfType(EMAIL_VERIFICATION_REQUIRED_ERROR, errors)) {
        // Credentials were valid but the user has not verified their email
        // yet. Route to the verify-email page. The hook layer appends the
        // user's email as a query param (the page requires it).
        resultType = LoginResultType.IS_EMAIL_VERIFICATION_REQUIRED;
        redirectUrl = urls.VERIFY_EMAIL_URL;
      } else {
        // User has cookies disabled and csrf cannot be set
        parsedErrors.error = 'It looks like you have cookies disabled';
        resultType = LoginResultType.IS_MISSING_CSRF;
        redirectUrl = urls.COOKIE_ERROR_URL;
      }
    }

    // See if we need to parse the errors array or not
    if (Array.isArray(errors) && errors.length > 0) {
      const firstError = errors[0];

      // Some errors are returned as a string
      if (firstError && typeof firstError === 'string') {
        parsedErrors.error = firstError;
        // We don't know what type of error this is so set UNKNOWN
        if (resultType === LoginResultType.IS_VALID_LOGIN) {
          resultType = LoginResultType.UNKNOWN_ERROR;
        }
      }

      // Contextual messages like "You are already logged in."
      // @ts-ignore
      if (firstError && typeof firstError === 'object' && firstError?.message) {
        // @ts-ignore
        parsedErrors.error = firstError?.message;
        if (resultType === LoginResultType.IS_VALID_LOGIN) {
          resultType = LoginResultType.UNKNOWN_ERROR;
        }
      }

      // One or more fields has returned an error
      errors.forEach(error => {
        if (typeof error === 'string') {
          return;
        }

        const fields = error?.fields;

        if (fields) {
          fields.forEach(field => {
            const key = fieldConversions[field.key] || field.key;
            // @ts-ignore
            parsedErrors[key] = field.message;
          });
        }
      });
      /**
       * We have an error, but we don't know how to deal with it. So throw
       * a generic error.
       */
      if (Object.keys(parsedErrors).length === 0) {
        throw resp;
      }
    }
  } catch (err) {
    /**
     * Something has blown up, maybe the API is down or the client might
     * be disconnected, or we have an error we cannot parse. In this case
     * send along a generic error.
     */
    parsedErrors.error = GENERIC_ERROR_MESSAGE;
    if (resultType === LoginResultType.IS_VALID_LOGIN) {
      resultType = LoginResultType.UNKNOWN_ERROR;
    }
  }

  if (resp.status === 422 && has(parsedErrors, 'captcha')) {
    resultType = LoginResultType.IS_CAPTCHA_REQUIRED;
  }

  return {
    errors: parsedErrors,
    redirectUrl,
    type: resultType as LoginResultType,
  };
}
